import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { LockOpen } from "@phosphor-icons/react";
import { unlockManifest } from "@/lib/api";
import { toast } from "sonner";

export default function UnlockManifestDialog({ storyId, onUnlocked }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await unlockManifest(storyId, note.trim() || null);
      toast.message("Manifest unlocked for revision", { description: note.trim() || "No reason given" });
      setOpen(false);
      setNote("");
      onUnlocked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unlock manifest");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        data-testid="unlock-manifest-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-amber-400 transition-colors border border-slate-700 rounded-sm px-2 py-1"
      >
        <LockOpen size={12} /> Unlock for Revision
      </button>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">Unlock Manifest</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            This reopens creative decisions on this manifest — shots become editable/reorderable/deletable again
            until you re-approve. The unlock is logged with a timestamp.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Reason (optional)</Label>
            <Textarea
              data-testid="unlock-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Fixing viewer embodiment contradiction on S01_SH01"
              rows={3}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="unlock-submit-btn" onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm w-full">
            {saving ? "Unlocking…" : "Unlock Manifest"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
